/**
 * The widget's own stylesheet, injected into its Shadow DOM (or into the light
 * DOM as a fallback). It carries every value it needs on `.vmai-root` and
 * inherits only the font family from the host page, so the host's global CSS
 * (villamine.com's Base.css) cannot restyle its controls and its own rules
 * cannot leak out. Brand tokens match villamine.com (2026-09-29). No font files
 * are bundled: the host's Gilroy is inherited where present.
 */
export const WIDGET_CSS = `
:host { all: initial; display: contents; font-family: "Gilroy", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; }
.vmai-root {
  --vmai-ink: #141414;
  --vmai-ink-2: #222222;
  --vmai-ink-3: #383838;
  --vmai-paper: #ffffff;
  --vmai-muted: #b0b0b0;
  --vmai-line: #333333;
  --vmai-gold: #886e46;
  --vmai-gold-2: #cca76a;
  --vmai-z: 2147482000;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.55;
  color: var(--vmai-paper);
  -webkit-font-smoothing: auto;
  -moz-osx-font-smoothing: auto;
  text-rendering: optimizeLegibility;
  font-synthesis: none;
  text-align: left;
  letter-spacing: normal;
}
.vmai-root *, .vmai-root *::before, .vmai-root *::after { box-sizing: border-box; }
.vmai-root button, .vmai-root input { font: inherit; color: inherit; margin: 0; }
.vmai-root h3, .vmai-root h4, .vmai-root p, .vmai-root ul { margin: 0; padding: 0; }
.vmai-root ul { list-style: none; }
.vmai-root a { color: inherit; }

.vmai-mark { color: var(--vmai-gold-2); font-size: 14px; line-height: 1; }

.vmai-pill {
  position: fixed; right: 24px; bottom: 82px; z-index: var(--vmai-z);
  display: flex; align-items: center; gap: 10px;
  padding: 12px 18px 12px 16px;
  border: 1px solid rgba(189, 154, 95, 0.45);
  background: rgba(25, 25, 25, 0.96); color: var(--vmai-paper);
  border-radius: 999px; box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
  cursor: pointer; font-family: inherit; font-size: 13.5px; font-weight: 500; line-height: 1.2;
  transition: border-color 160ms ease, transform 160ms ease;
}
.vmai-pill:hover { border-color: var(--vmai-gold-2); transform: translateY(-1px); }
.vmai-pill__brand { font-size: 13px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: #ffffff; }
.vmai-pill__tagline { font-size: 13px; color: #b8b8b8; border-left: 1px solid var(--vmai-line); padding-left: 10px; }

.vmai-panel {
  position: fixed; right: 24px; bottom: 82px; z-index: var(--vmai-z);
  width: 440px; max-width: calc(100vw - 48px); height: min(720px, calc(100vh - 104px));
  display: flex; flex-direction: column;
  background: #191919; color: var(--vmai-paper);
  border: 1px solid #333333; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  border-radius: 8px; overflow: hidden; font-family: inherit;
}
.vmai-panel__header { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid #303030; background: #161616; }
.vmai-panel__title { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: #ffffff; }
.vmai-panel__actions { display: flex; align-items: center; gap: 8px; }
.vmai-textbtn { background: none; border: 0; color: #b0b0b0; font-size: 12.5px; font-weight: 500; cursor: pointer; font-family: inherit; padding: 4px 8px; border-radius: 4px; }
.vmai-textbtn:hover { color: var(--vmai-gold-2); background: rgba(255, 255, 255, 0.05); }
.vmai-iconbtn { background: none; border: 0; color: #b0b0b0; font-size: 24px; line-height: 1; cursor: pointer; padding: 0 6px; }
.vmai-iconbtn:hover { color: #ffffff; }

.vmai-panel__scroll { flex: 1; overflow-y: auto; padding: 16px 18px 12px; scrollbar-width: thin; scrollbar-color: #383838 transparent; }

.vmai-chips { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 16px; }
.vmai-chip { max-width: 100%; overflow-wrap: anywhere; display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; padding: 6px 10px 6px 12px; border: 1px solid rgba(204, 167, 106, 0.4); border-radius: 999px; color: #f0e6d6; background: rgba(204, 167, 106, 0.08); }
.vmai-chip button { background: none; border: 0; color: #b8b8b8; cursor: pointer; font-size: 15px; line-height: 1; padding: 0; }
.vmai-chip button:hover { color: #ffffff; }

.vmai-status { display: flex; align-items: center; gap: 10px; font-size: 13.5px; color: #c4c4c4; margin: 8px 0 16px; }
.vmai-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--vmai-gold-2); animation: vmai-pulse 1.1s ease-in-out infinite; }
@keyframes vmai-pulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }

.vmai-reply { font-size: 14.5px; font-weight: 400; line-height: 1.65; margin: 6px 0 16px; color: #ffffff; overflow-wrap: anywhere; letter-spacing: 0.01em; }
.vmai-reply p { margin: 0 0 10px; }
.vmai-reply.is-error { color: #f2a6a2; }
.vmai-notice { overflow-wrap: anywhere; font-size: 12px; line-height: 1.55; color: #b8b8b8; margin: 0 0 10px; padding: 4px 0 4px 12px; border-left: 2px solid #383838; }
.vmai-notice.is-demo { border-left-color: var(--vmai-gold); color: #d6be95; }

.vmai-intro { color: #cfcfcf; font-size: 13.5px; line-height: 1.6; padding: 6px 0; }
.vmai-intro ul { margin: 14px 0 0; display: grid; gap: 10px; }
.vmai-intro li { border: 1px solid #333333; background: #222222; padding: 10px 12px; border-radius: 5px; color: #ffffff; font-size: 13px; line-height: 1.5; }

.vmai-results__title { font-size: 11.5px; letter-spacing: 0.12em; text-transform: uppercase; color: #a8a8a8; margin: 18px 0 10px; display: flex; align-items: center; gap: 8px; font-weight: 600; }
.vmai-results__title span { color: var(--vmai-gold-2); font-weight: 700; }
.vmai-results.is-partial .vmai-card { opacity: 0.9; }

.vmai-card { display: grid; grid-template-columns: 108px 1fr; gap: 14px; padding: 12px; margin-bottom: 12px; border: 1px solid #333333; border-radius: 6px; background: #222222; transition: border-color 160ms ease; }
.vmai-card:hover { border-color: rgba(204, 167, 106, 0.6); }
.vmai-card__media { display: block; width: 108px; height: 82px; overflow: hidden; border-radius: 4px; background: #181818; }
.vmai-card__media img { width: 100%; height: 100%; object-fit: cover; display: block; border: 0; }
.vmai-card__noimg { display: block; width: 100%; height: 100%; }
.vmai-card__body { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.vmai-card__top { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin-bottom: 1px; }
.vmai-card__name { font-size: 14.5px; font-weight: 600; color: #ffffff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; letter-spacing: 0.01em; }
.vmai-card__place, .vmai-card__facts { font-size: 12.5px; line-height: 1.45; color: #bfbfbf; overflow-wrap: anywhere; }
.vmai-card__tags { display: flex; flex-wrap: wrap; gap: 5px; margin: 5px 0 0; }
.vmai-tag { font-size: 11px; line-height: 1.3; padding: 3px 8px; border-radius: 999px; border: 1px solid #3d3d3d; color: #dedede; }
.vmai-tag.is-confirmed { border-color: rgba(204, 167, 106, 0.6); color: #f0debe; background: rgba(204, 167, 106, 0.08); }
.vmai-tag.is-derived { border-style: dotted; border-color: rgba(204, 167, 106, 0.6); color: #ded0b6; }
.vmai-tag.is-unconfirmed { border-style: dashed; color: #9c9c9c; }
.vmai-card__bottom { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: auto; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.06); }
.vmai-card__price { font-size: 13px; font-weight: 600; color: #ffffff; }
.vmai-card__link { font-size: 12.5px; font-weight: 500; color: var(--vmai-gold-2); text-decoration: none; white-space: nowrap; }
.vmai-card__link:hover { text-decoration: underline; color: #e2ba7c; }
.vmai-badge { font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; padding: 3px 8px; border-radius: 3px; white-space: nowrap; }
.vmai-badge.is-available { color: #bfe3b6; background: rgba(105, 174, 94, 0.22); border: 1px solid rgba(105, 174, 94, 0.35); }
.vmai-badge.is-unavailable { color: #f0b3ae; background: rgba(163, 47, 42, 0.3); border: 1px solid rgba(163, 47, 42, 0.4); }
.vmai-badge.is-unknown { color: #b0b0b0; background: rgba(140, 140, 140, 0.18); }

.vmai-relax { margin: 8px 0 16px; }
.vmai-relax__title { font-size: 13px; font-weight: 500; color: #dedede; margin: 0 0 10px; }
.vmai-relax__options { display: flex; flex-wrap: wrap; gap: 8px; }
.vmai-relax__btn { display: inline-flex; align-items: center; gap: 8px; font-size: 12.5px; padding: 7px 12px; border: 1px solid rgba(204, 167, 106, 0.45); background: rgba(204, 167, 106, 0.06); color: #f0e6d6; border-radius: 4px; cursor: pointer; font-family: inherit; }
.vmai-relax__btn:hover { border-color: var(--vmai-gold-2); background: rgba(204, 167, 106, 0.12); }
.vmai-relax__count { color: var(--vmai-gold-2); font-size: 11.5px; font-weight: 600; }

.vmai-composer { display: flex; gap: 10px; padding: 14px 18px 8px; border-top: 1px solid #303030; background: #161616; }
.vmai-composer input { flex: 1; min-width: 0; background: #202020; border: 1px solid #383838; color: #ffffff; padding: 12px 14px; font-size: 14px; line-height: 1.4; border-radius: 4px; font-family: inherit; outline: none; appearance: none; -webkit-appearance: none; height: auto; }
.vmai-composer input:focus { border-color: var(--vmai-gold-2); box-shadow: 0 0 0 1px var(--vmai-gold-2); }
.vmai-composer input::placeholder { color: #888888; }
.vmai-composer button { background: var(--vmai-gold); color: #ffffff; border: 0; padding: 0 18px; font-size: 12.5px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; border-radius: 4px; cursor: pointer; font-family: inherit; transition: background 150ms ease; }
.vmai-composer button:hover:not(:disabled) { background: var(--vmai-gold-2); }
.vmai-composer button:disabled { opacity: 0.45; cursor: default; }
.vmai-footer { font-size: 11px; color: #888888; text-align: center; padding: 2px 18px 12px; }
.vmai-quota { font-size: 11px; line-height: 1.4; color: #a0a0a0; margin: 0; padding: 4px 18px 0; text-align: right; letter-spacing: 0.02em; }
.vmai-limit-notice { font-size: 12.5px; line-height: 1.6; color: #e8e8e8; margin: 0 0 12px; padding: 10px 12px; border-left: 3px solid var(--vmai-gold-2); background: rgba(204, 167, 106, 0.08); border-radius: 0 4px 4px 0; }
.vmai-exhausted { padding: 14px 18px 8px; color: #e0e0e0; font-size: 13.5px; line-height: 1.6; }
.vmai-exhausted p { margin: 0 0 12px; }
.vmai-contact { display: inline-block; padding: 9px 16px; border: 1px solid var(--vmai-gold-2); border-radius: 4px; color: #ffffff; background: rgba(204, 167, 106, 0.15); text-decoration: none; font-size: 13px; font-weight: 500; }
.vmai-contact:hover { background: rgba(204, 167, 106, 0.28); color: #ffffff; }

@media (max-width: 640px) {
  .vmai-pill { left: 16px; right: 16px; bottom: calc(75px + env(safe-area-inset-bottom)); justify-content: center; padding: 14px 16px; }
  .vmai-pill__tagline { display: none; }
  .vmai-panel { left: 0; right: 0; bottom: 0; width: 100%; max-width: none; height: 88dvh; border-radius: 14px 14px 0 0; padding-bottom: env(safe-area-inset-bottom); }
  .vmai-card { grid-template-columns: 92px 1fr; gap: 10px; padding: 10px; }
  .vmai-card__media { width: 92px; height: 74px; }
}
`;
